/**
 * Invitations Dashboard — the user-facing surface of the Invitation
 * Management System. Renders the stats strip, tabs (All/Active/Inactive/
 * Drafts/Archived), filters, paginated card grid, and the timeline modal.
 *
 * State strategy:
 *   - URL-derived query state is intentionally kept in component state (not
 *     URLSearchParams) because the wider app uses simple state-based pages;
 *     adding URL sync here would be a one-off pattern. Easy to add later.
 *   - `refreshKey` increments on every successful lifecycle action so the
 *     stats strip re-fetches (cards refresh because the list hook re-runs).
 */
import { Fragment, useCallback, useEffect, useMemo, useState } from 'react';
import { Link, useLocation, useNavigate } from 'react-router-dom';
import { PAYMENTS_CONFIRMED_EVENT } from '@/src/modules/purchase/usePendingPaymentConfirmation';
import { DashboardHeader } from '@/src/modules/components/Header';
import { Icons } from '@/src/modules/components/Icons';
import { ConfirmModal } from '@/src/modules/components/settings/ConfirmModal';
import { InsufficientWalletModal } from '@/src/modules/components/InsufficientWalletModal';
import { Button } from '@/src/modules/components/Button';
import { GradientButton } from '@/src/modules/components/ui/gradient-button';
import { useAuth } from '@/src/modules/auth/AuthProvider';
import { useIsPartner } from '@/src/modules/auth/usePermissions';
import { extendCostLabel } from './extendCost';
import { ApiError } from '@/src/infrastructure/lib/api';
import type {
  ApiInvitation,
  BackendOccasionType,
  InvitationSortBy,
  InvitationTab,
} from '@/src/types';
import {
  invitationActions,
  useInvitationStats,
  useInvitationsList,
} from './hooks/useInvitations';
import { InvitationStatsStrip } from './components/InvitationStats';
import { InvitationFilters } from './components/InvitationFilters';
import { InvitationCard } from './components/InvitationCard';
import { InvitationEmptyState } from './components/InvitationEmptyState';
import { InvitationHistoryModal } from './components/InvitationHistoryModal';
import { ShareInvitationModal } from './components/ShareInvitationModal';
import { DraftPreviewModal, type DraftPreviewData } from '@/src/modules/pages/wizard/DraftPreviewModal';

const PAGE_SIZE = 12;
const EMPTY_ARRAY: ApiInvitation[] = [];

export function InvitationsPage() {
  const { user } = useAuth();
  const isPartner = useIsPartner();
  const location = useLocation();
  const navigate = useNavigate();

  // Allow deep-linking into a specific tab via ?tab=drafts etc.
  const initialTab = (new URLSearchParams(location.search).get('tab') as InvitationTab) || 'all';
  const [tab, setTab] = useState<InvitationTab>(initialTab);
  const [search, setSearch] = useState('');
  const [occasionType, setOccasionType] = useState<BackendOccasionType | ''>('');
  const [sortBy, setSortBy] = useState<InvitationSortBy>('createdAt');
  const [sortOrder, setSortOrder] = useState<'asc' | 'desc'>('desc');
  const [page, setPage] = useState(1);

  // Lifecycle actions optimistically reset to page 1 + bump the stats key
  // so counts stay in sync with the visible grid.
  const [refreshKey, setRefreshKey] = useState(0);
  // A payment confirmed on return (see usePendingPaymentConfirmation) changes
  // what's "Paid · not published" — refresh the list and counts.
  useEffect(() => {
    const onConfirmed = () => setRefreshKey((k) => k + 1);
    window.addEventListener(PAYMENTS_CONFIRMED_EVENT, onConfirmed);
    return () => window.removeEventListener(PAYMENTS_CONFIRMED_EVENT, onConfirmed);
  }, []);
  const [busyId, setBusyId] = useState<string | null>(null);
  const [historyInvitation, setHistoryInvitation] = useState<ApiInvitation | null>(null);
  const [shareInvitation, setShareInvitation] = useState<ApiInvitation | null>(null);
  const [pendingDelete, setPendingDelete] = useState<ApiInvitation | null>(null);
  const [cancelRequest, setCancelRequest] = useState<{ id: string; eventName: string } | null>(null);
  const [previewData, setPreviewData] = useState<DraftPreviewData | null>(null);
  const [toast, setToast] = useState<string | null>(null);

  const query = useMemo(
    () => ({
      page,
      limit: PAGE_SIZE,
      tab,
      search: search.trim() || undefined,
      occasionType: (occasionType || undefined) as BackendOccasionType | undefined,
      sortBy,
      sortOrder,
    }),
    [page, tab, search, occasionType, sortBy, sortOrder],
  );

  const { data, loading, error } = useInvitationsList(query, refreshKey);
  const { stats } = useInvitationStats(refreshKey);

  const items = data?.items ?? EMPTY_ARRAY;
  const meta = data?.meta;

  const reset = useCallback(() => {
    setTab('all');
    setSearch('');
    setOccasionType('');
    setSortBy('createdAt');
    setSortOrder('desc');
    setPage(1);
  }, []);

  const onAction = useCallback(
    async (id: string, action: 'archive' | 'restore' | 'cancel') => {
      setBusyId(id);
      try {
        if (action === 'archive') await invitationActions.archive(id);
        else if (action === 'restore') await invitationActions.restore(id);
        else {
          const reason = window.prompt('Reason for cancellation (optional)?');
          if (reason === null) {
            // User clicked Cancel on prompt — do not cancel invitation (PQ-22)
            return;
          }
          await invitationActions.cancel(id, reason.trim() || undefined);
        }
        setToast(
          action === 'archive'
            ? 'Invitation archived'
            : action === 'restore'
              ? 'Invitation restored'
              : 'Invitation cancelled',
        );
        setRefreshKey((k) => k + 1);
      } catch (err) {
        setToast(err instanceof Error ? err.message : 'Action failed');
      } finally {
        setBusyId(null);
        setTimeout(() => setToast(null), 2400);
      }
    },
    [],
  );

  const copyLink = useCallback(async (url: string) => {
    await navigator.clipboard.writeText(url);
    setToast('Link copied');
    setTimeout(() => setToast(null), 1800);
  }, []);

  const onEdit = useCallback(
    (id: string) => navigate(`/wizard?draftId=${encodeURIComponent(id)}`),
    [navigate],
  );

  const onCapsuleGift = useCallback(
    (id: string) => navigate(`/dashboard/invitations/${encodeURIComponent(id)}/capsule-gift`),
    [navigate],
  );

  const onPreview = useCallback((invitation: ApiInvitation) => {
    setPreviewData({
      templateId: invitation.templateId,
      occasionType: invitation.occasionType,
      eventName: invitation.eventName,
      eventDate: invitation.eventDate,
      eventVenue: invitation.eventVenue,
      hostName: invitation.hostName,
      customMessage: invitation.customMessage,
      rsvpLink: invitation.rsvpLink,
      mediaUrls: invitation.mediaUrls,
      customData: invitation.customData as Record<string, unknown> | null | undefined,
    });
  }, []);

  const confirmDelete = useCallback(async () => {
    if (!pendingDelete) return;
    const id = pendingDelete.id;
    setBusyId(id);
    setPendingDelete(null);
    try {
      await invitationActions.delete(id);
      setToast('Draft deleted');
      setRefreshKey((k) => k + 1);
    } catch (err) {
      setToast(err instanceof Error ? err.message : 'Delete failed');
    } finally {
      setBusyId(null);
      setTimeout(() => setToast(null), 2400);
    }
  }, [pendingDelete]);

  const handleArchive = useCallback((id: string) => {
    void onAction(id, 'archive');
  }, [onAction]);

  const handleRestore = useCallback((id: string) => {
    void onAction(id, 'restore');
  }, [onAction]);

  const handleCancel = useCallback(
    (id: string) => {
      const match = items.find((i) => i.id === id);
      setCancelRequest({ id, eventName: match?.eventName?.trim() || 'This invitation' });
    },
    [items],
  );

  const confirmCancel = useCallback(async () => {
    if (!cancelRequest) return;
    const { id } = cancelRequest;
    setBusyId(id);
    setCancelRequest(null);
    try {
      await invitationActions.cancel(id);
      setToast('Invitation cancelled');
      setRefreshKey((k) => k + 1);
    } catch (err) {
      setToast(err instanceof Error ? err.message : 'Action failed');
    } finally {
      setBusyId(null);
      setTimeout(() => setToast(null), 2400);
    }
  }, [cancelRequest]);

  // Extending spends money (2 publish credits, or ₹500 from the wallet — ADR-033): always ask first, with the real price.
  const [extendRequest, setExtendRequest] = useState<{ id: string; days: number } | null>(null);
  const [insufficientBalance, setInsufficientBalance] = useState<{
    isWalletUser: boolean;
    neededRupees?: number;
    availableRupees?: number;
    neededCredits?: number;
    availableCredits?: number;
    message?: string;
  } | null>(null);
  const requestExtend = useCallback((id: string, extensionDays: number) => setExtendRequest({ id, days: extensionDays }), []);

  const handleExtend = useCallback(
    (id: string, extensionDays: number) => {
      setBusyId(id);
      invitationActions
        .extend(id, extensionDays)
        .then(() => {
          setToast('Invitation extended');
          setRefreshKey((k) => k + 1);
        })
        .catch((err) => {
          const status = (err as { status?: number })?.status;
          const errData = (err as ApiError)?.data as { code?: string; error?: { code?: string } } | undefined;
          const code = errData?.code || errData?.error?.code;
          if (status === 402 || code === 'NO_PUBLISH_RIGHT') {
            setInsufficientBalance({
              isWalletUser: isPartner,
              message: err instanceof Error ? err.message : undefined,
            });
            return;
          }
          setToast(err instanceof Error ? err.message : 'Extension failed');
        })
        .finally(() => {
          setBusyId(null);
          setTimeout(() => setToast(null), 2400);
        });
    },
    [isPartner],
  );

  const handleShare = useCallback((invitation: ApiInvitation) => {
    setShareInvitation(invitation);
  }, []);

  const handleDelete = useCallback((id: string) => {
    const inv = data?.items?.find((i) => i.id === id);
    if (inv) setPendingDelete(inv);
  }, [data?.items]);

  const handleOpenHistory = useCallback((id: string) => {
    const inv = data?.items?.find((i) => i.id === id);
    if (inv) setHistoryInvitation(inv);
  }, [data?.items]);

  const handleCopyLink = useCallback((url: string) => {
    void copyLink(url);
  }, [copyLink]);
  return (
    <div className="flex-1 flex flex-col min-h-screen bg-surface-lowest pb-20 md:pb-8">
      <DashboardHeader
        title="My Invitations"
        description={`Welcome back${user?.displayName ? ', ' + user.displayName : ''}. Track, filter, and manage every invitation in one place.`}
      />

      <div className="px-8 py-8 max-w-7xl mx-auto w-full">
        <div className="flex items-center justify-between mb-6">
          <div />
          <Link to="/wizard">
            <GradientButton className="flex items-center gap-2">
              <Icons.Plus size={18} />
              Create invitation
            </GradientButton>
          </Link>
        </div>

        <InvitationStatsStrip 
          stats={stats} 
          tab={tab} 
          onTabChange={(t) => {
            setTab(t);
            setPage(1);
          }}
        />

        <InvitationFilters
          search={search}
          onSearchChange={(v) => {
            setSearch(v);
            setPage(1);
          }}
          occasionType={occasionType}
          onOccasionChange={(o) => {
            setOccasionType(o);
            setPage(1);
          }}
          sortBy={sortBy}
          onSortByChange={(s) => {
            setSortBy(s);
            setPage(1);
          }}
          sortOrder={sortOrder}
          onSortOrderChange={setSortOrder}
          onReset={reset}
        />

        {loading && (
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
            {Array.from({ length: 6 }).map((_, i) => (
              <div
                key={i}
                className="h-72 rounded-[2rem] bg-surface border border-outline-variant/30 animate-pulse"
              />
            ))}
          </div>
        )}

        {!loading && error && (
          <div className="text-center py-16 text-status-failed font-medium">{error}</div>
        )}

        {!loading && !error && items.length === 0 && <InvitationEmptyState tab={tab} />}

        {!loading && !error && items.length > 0 && (
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
            {items.map((invitation) => (
              <Fragment key={invitation.id}>
                <InvitationCard
                  invitation={invitation}
                  busy={busyId === invitation.id}
                  onArchive={handleArchive}
                  onRestore={handleRestore}
                  onCancel={handleCancel}
                  onEdit={onEdit}
                  onPreview={onPreview}
                  onShare={handleShare}
                  onDelete={handleDelete}
                  onOpenHistory={handleOpenHistory}
                  onCopyLink={handleCopyLink}
                  onCapsuleGift={onCapsuleGift}
                  onExtend={requestExtend}
                />
              </Fragment>
            ))}
          </div>
        )}

        {meta && meta.totalPages > 1 && (
          <div className="mt-8 flex items-center justify-center gap-3">
            <Button
              variant="outline"
              size="sm"
              disabled={meta.page <= 1}
              onClick={() => setPage((p) => Math.max(1, p - 1))}
            >
              <Icons.ChevronLeft size={16} />
              Prev
            </Button>
            <span className="text-sm text-on-surface-variant font-medium">
              Page {meta.page} of {meta.totalPages}
            </span>
            <Button
              variant="outline"
              size="sm"
              disabled={meta.page >= meta.totalPages}
              onClick={() => setPage((p) => p + 1)}
            >
              Next
              <Icons.ChevronRight size={16} />
            </Button>
          </div>
        )}
      </div>

      {historyInvitation && (
        <InvitationHistoryModal
          invitationId={historyInvitation.id}
          invitationName={historyInvitation.eventName}
          onClose={() => setHistoryInvitation(null)}
        />
      )}

      <ConfirmModal
        open={extendRequest !== null}
        tone="primary"
        title="Extend this invitation?"
        description={`This restores the invitation for ${extendRequest?.days ?? 7} days. It costs ${extendCostLabel(isPartner)}.`}
        confirmLabel="Extend"
        onClose={() => setExtendRequest(null)}
        onConfirm={() => {
          if (extendRequest) handleExtend(extendRequest.id, extendRequest.days);
          setExtendRequest(null);
        }}
      />

      <ConfirmModal
        open={cancelRequest !== null}
        tone="danger"
        title="Cancel this invitation?"
        description={`"${cancelRequest?.eventName || 'This invitation'}" will be unpublished and cancelled. This cannot be undone.`}
        confirmLabel="Cancel Invitation"
        cancelLabel="Keep Invitation"
        onClose={() => setCancelRequest(null)}
        onConfirm={confirmCancel}
      />

      <InsufficientWalletModal
        open={insufficientBalance !== null}
        isWalletUser={insufficientBalance?.isWalletUser}
        neededRupees={insufficientBalance?.neededRupees}
        availableRupees={insufficientBalance?.availableRupees}
        neededCredits={insufficientBalance?.neededCredits}
        availableCredits={insufficientBalance?.availableCredits}
        message={insufficientBalance?.message}
        onClose={() => setInsufficientBalance(null)}
      />

      {shareInvitation && (
        <ShareInvitationModal
          invitation={shareInvitation}
          onClose={() => setShareInvitation(null)}
        />
      )}

      {/* Draft preview modal */}
      {previewData && (
        <DraftPreviewModal
          open={!!previewData}
          data={previewData}
          onClose={() => setPreviewData(null)}
          ctaLabel="Continue editing"
          onCta={() => {
            if (previewData) {
              const match = data?.items?.find(
                (inv) => inv.templateId === previewData.templateId && inv.eventName === previewData.eventName,
              );
              if (match) {
                setPreviewData(null);
                navigate(`/wizard?draftId=${encodeURIComponent(match.id)}`);
              }
            }
          }}
        />
      )}

      {pendingDelete && (
        <div
          className="fixed inset-0 z-[100] flex items-center justify-center bg-black/40 p-4"
          onClick={() => setPendingDelete(null)}
        >
          <div
            className="w-full max-w-md rounded-3xl bg-surface-lowest p-6 shadow-xl"
            onClick={(e) => e.stopPropagation()}
          >
            <h2 className="text-lg font-bold text-on-surface">Delete this draft?</h2>
            <p className="mt-2 text-sm text-on-surface-variant">
              "{pendingDelete.eventName?.trim() || 'Untitled draft'}" will be permanently removed.
              This can't be undone.
            </p>
            <div className="mt-6 flex justify-end gap-3">
              <Button variant="outline" onClick={() => setPendingDelete(null)}>
                Cancel
              </Button>
              <Button
                onClick={() => void confirmDelete()}
                className="bg-status-failed text-white hover:bg-status-failed/90"
              >
                Delete
              </Button>
            </div>
          </div>
        </div>
      )}

      {toast && (
        <div className="fixed bottom-6 right-6 z-50 bg-on-surface text-surface px-4 py-2 rounded-full shadow-lg text-sm font-medium">
          {toast}
        </div>
      )}
    </div>
  );
}

export default InvitationsPage;
